/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel, PublicClientApplication, Configuration } from "@azure/msal-browser";

/**
 * Configuration object to be passed to MSAL instance on creation. 
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md 
 */
export const createMsalConfig = (clientId: string, tenantId: string): Configuration => ({
    auth: {
        clientId: clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin
    },
    cache: {
        cacheLocation: "sessionStorage", // This configures where your cache will be stored
        storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },
    system: {	
        loggerOptions: {	
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {	
                if (containsPii) {		
                    return;		
                }		
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }	
            }	
        }	
    }
});

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit: 
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    scopes: ["User.Read"]
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me" //e.g. https://graph.microsoft.com/v1.0/me
};

/**
 * Create the main myMSALObj instance
 * MSAL.js v2 exposes several application APIs that allow a developer to instantiate Public Client Applications
 * and other methods to facilitate authentication/authorization.
 */
// export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Creates a new MSAL instance with dynamic configuration from theme config
 */
export const createMsalInstance = (clientId: string, tenantId: string): PublicClientApplication => {
    const config = createMsalConfig(clientId, tenantId);
    return new PublicClientApplication(config);
};

// Store the dynamic instance for reuse
let dynamicMsalInstance: PublicClientApplication | null = null;

/**
 * Gets or creates the dynamic MSAL instance
 */
export const getDynamicMsalInstance = (clientId?: string, tenantId?: string): PublicClientApplication => {
    if (clientId && tenantId && (!dynamicMsalInstance || 
        dynamicMsalInstance.getConfiguration().auth.clientId !== clientId)) {
        dynamicMsalInstance = createMsalInstance(clientId, tenantId);
    }
    return dynamicMsalInstance || createMsalInstance(clientId ?? "", tenantId ?? "");
};
